import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { STUDIO_ACTIONS } from '../constants';
import { AI_STUDIO_FIELDS, MEDIA_TYPES } from '../../../constants';

const useAdditionalFields = (mediaType = MEDIA_TYPES.IMAGE) => {
  const { t } = useTranslation('studio');

  const imagesRatioOptions = useSelector((state) => state.aiStudio.imagesRatio);
  const videoRatio = useSelector((state) => state.aiStudio.videoRatio);
  const freeTextVideoRatio = useSelector((state) => state.aiStudio.freeTextVideoRatio);

  const CHANGE_FIELDS = [
    {
      name: 'from',
      label: t('from'),
      placeholder: t('from'),
      type: AI_STUDIO_FIELDS.TEXT,
      isRequired: true,
      size: 'md',
    },
    {
      name: 'to',
      label: t('to'),
      type: AI_STUDIO_FIELDS.TEXT,
      isRequired: true,
      size: 'md',
    },
    {
      name: 'doNotChange',
      label: t('doNotChange'),
      type: AI_STUDIO_FIELDS.TEXT,
      isRequired: false,
      size: 'bg',
    },
    {
      name: 'message',
      label: t('message'),
      type: AI_STUDIO_FIELDS.CHAT,
      isRequired: false,
      disableAttachments: true,
    },
  ];

  return {
    [STUDIO_ACTIONS.FREE_TEXT_EDIT]: [
      ...(mediaType === MEDIA_TYPES.VIDEO
        ? [
            {
              name: 'audio',
              label: t('audio'),
              type: AI_STUDIO_FIELDS.SWITCH,
              size: 'md',
              isRequired: true,
              subLabels: {
                true: t('on'),
                false: t('off'),
              },
            },
          ]
        : []),
      ...(mediaType === MEDIA_TYPES.VIDEO
        ? [
            {
              name: 'ratio',
              label: t('ratio'),
              type: AI_STUDIO_FIELDS.DROPDOWN,
              size: 'bg',
              isRequired: true,
              options: freeTextVideoRatio,
            },
          ]
        : []),

      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: true,
        disableAttachments: mediaType === MEDIA_TYPES.VIDEO ? true : false,
      },
    ],
    [STUDIO_ACTIONS.ADD_LOGO]: [
      {
        name: 'file',
        label: t('file'),
        type: AI_STUDIO_FIELDS.FILE,
        isRequired: true,
        acceptedFileTypes: { 'image/*': [] },
        size: 'bg',
      },
      {
        name: 'size',
        label: t('size'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'position',
        label: t('position'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.ADD_TEXT]: [
      {
        name: 'file',
        label: t('file'),
        type: AI_STUDIO_FIELDS.FILE,
        isRequired: false,
        acceptedFileTypes: { 'image/*': [] },
        size: 'bg',
      },
      {
        name: 'size',
        label: t('size'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'position',
        label: t('position'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.ADD_VISUAL_ELEMENTS_FROM_TEXT]: [
      {
        name: 'file',
        label: t('file'),
        type: AI_STUDIO_FIELDS.FILE,
        isRequired: false,
        acceptedFileTypes: { 'image/*': [] },
        size: 'bg',
      },
      {
        name: 'size',
        label: t('size'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'position',
        label: t('position'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.ADD_VISUAL_ELEMENTS_FROM_IMAGE]: [
      {
        name: 'file',
        label: t('file'),
        type: AI_STUDIO_FIELDS.FILE,
        isRequired: true,
        acceptedFileTypes: { 'image/*': [] },
        size: 'bg',
      },
      {
        name: 'size',
        label: t('size'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'position',
        label: t('position'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'md',
      },
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.ADD_ROOM_FOR_TEXT]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.ADD_REALISM]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],

    [STUDIO_ACTIONS.CHANGE_SETTING]: CHANGE_FIELDS,
    [STUDIO_ACTIONS.CHANGE_ELEMENT]: CHANGE_FIELDS,
    [STUDIO_ACTIONS.CHANGE_BRAND_LOOK]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: false,
        isFileRequired: true,
        acceptedFileTypes: { 'image/*': [] },
      },
    ],
    [STUDIO_ACTIONS.CHANGE_STYLE]: CHANGE_FIELDS,
    [STUDIO_ACTIONS.CHANGE_CUTOUT_ELEMENT]: [
      {
        name: 'cutout',
        label: t('elementToCutout'),
        placeholder: t('elementToCutout'),
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'bg',
      },
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.CREATE_FORMATS]: [
      {
        name: 'format',
        label: t('format'),
        type: AI_STUDIO_FIELDS.DROPDOWN,
        size: 'bg',
        isRequired: true,
        options: mediaType === MEDIA_TYPES.VIDEO ? videoRatio : imagesRatioOptions,
      },
      ...(mediaType === MEDIA_TYPES.IMAGE
        ? [
            {
              name: 'width',
              label: t('width'),
              type: AI_STUDIO_FIELDS.TEXT,
              isRequired: true,
              size: 'md',
              showWhen: { field: 'format', value: 'CUSTOM' },
            },
            {
              name: 'height',
              label: t('height'),
              type: AI_STUDIO_FIELDS.TEXT,
              isRequired: true,
              size: 'md',
              showWhen: { field: 'format', value: 'CUSTOM' },
            },
          ]
        : []),
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.CREATE_ANGLES]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.CREATE_VARIANTS]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: true,
      },
    ],
    [STUDIO_ACTIONS.CONCEPT_RECREATE]: [
      {
        name: 'url',
        label: 'URL',
        type: AI_STUDIO_FIELDS.TEXT,
        isRequired: true,
        size: 'bg',
      },
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: true,
        disableAttachments: false,
      },
    ],
    [STUDIO_ACTIONS.MERGE_IMAGES]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: true,
        isFileRequired: true,
        disableAttachments: false,
        acceptedFileTypes: { 'image/*': [] },
      },
    ],
    [STUDIO_ACTIONS.CREATE_VIDEO]: [
      {
        name: 'message',
        label: t('message'),
        type: AI_STUDIO_FIELDS.CHAT,
        isRequired: false,
        disableAttachments: false,
      },
    ],
  };
};

export default useAdditionalFields;
